import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, query, getDocs, orderBy, where, doc, getDoc, startAfter, limit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { EyeIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/solid';
import { getPrintJobStatus } from '../../services/printService/luluApi';

const AdminPrintJobsPage = () => {
  const [printJobs, setPrintJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [uniqueStatuses, setUniqueStatuses] = useState(['all', 'PAID_AWAITING_FULFILLMENT']);
  const [pageCursors, setPageCursors] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  
  // Helper function to determine the effective status combining Lulu API status and payment status
  const getEffectiveStatus = (job) => {
    // If no job or missing critical fields
    if (!job) {
      return { name: 'UNKNOWN', message: 'Missing job data' };
    }
    
    // Start with payment status
    const paymentStatus = job.paymentStatus || 'UNKNOWN';
    
    // Get Lulu API status if available
    const luluStatus = job.details?.status || job.status || null;
    
    // Payment status takes precedence for initial states
    if (paymentStatus === 'PAID_AWAITING_FULFILLMENT') {
      return { 
        name: 'PAID_AWAITING_FULFILLMENT', 
        message: luluStatus ? `Lulu status: ${luluStatus}` : 'Awaiting processing' 
      };
    }
    
    if (paymentStatus === 'PAYMENT_FAILED') {
      return { name: 'PAYMENT_FAILED', message: 'Payment transaction failed' };
    }
    
    // If we have Lulu API status, use that for later states
    if (luluStatus) {
      switch (luluStatus) {
        case 'CREATED':
          return { name: 'PROCESSING', message: 'Print job created at Lulu' };
        case 'ACCEPTED':
          return { name: 'ACCEPTED', message: 'Print job accepted by Lulu' };
        case 'REJECTED':
          return { name: 'REJECTED', message: 'Print job rejected by Lulu' };
        case 'IN_PRODUCTION':
          return { name: 'IN_PRODUCTION', message: 'Book is being printed' };
        case 'SHIPPED':
          return { name: 'SHIPPED', message: 'Book has been shipped' };
        case 'CANCELLED':
          return { name: 'CANCELLED', message: 'Print job was cancelled' };
        case 'ERROR':
          return { name: 'ERROR', message: 'Error in print job processing' };
        case 'PRODUCTION_DELAYED':
          return { name: 'PRODUCTION_DELAYED', message: 'Production is delayed' };
        default:
          return { name: luluStatus, message: 'Status from Lulu API' };
      }
    }
    
    // Default fallback if we don't have Lulu status
    return { name: paymentStatus || 'UNKNOWN', message: 'Status based on payment' };
  };
  
  // Helper function for status badge CSS classes
  const getStatusBadgeClass = (status) => {
    if (!status) return 'bg-gray-50 border border-gray-200 text-gray-700';
    
    // Convert status to string for safety and comparison
    const statusStr = String(status);
    
    switch (statusStr) {
      case 'PAID_AWAITING_FULFILLMENT':
        return 'bg-yellow-50 border border-yellow-200 text-yellow-800';
      case 'PAYMENT_FAILED':
        return 'bg-red-50 border border-red-200 text-red-800';
      case 'PROCESSING':
        return 'bg-blue-50 border border-blue-200 text-blue-800';
      case 'ACCEPTED':
        return 'bg-green-50 border border-green-200 text-green-800';
      case 'IN_PRODUCTION':
        return 'bg-blue-50 border border-blue-200 text-blue-800';
      case 'SHIPPED':
        return 'bg-green-50 border border-green-200 text-green-800';
      case 'REJECTED':
        return 'bg-red-50 border border-red-200 text-red-800';
      case 'CANCELLED':
        return 'bg-red-50 border border-red-200 text-red-800';
      case 'ERROR':
        return 'bg-red-50 border border-red-200 text-red-800';
      case 'PRODUCTION_DELAYED':
        return 'bg-amber-50 border border-amber-200 text-amber-800';
      default:
        return 'bg-gray-50 border border-gray-200 text-gray-700';
    }
  };

  // Function to fetch the latest Lulu status directly from the API
  const fetchLatestLuluStatus = async (printJobId) => {
    try {
      console.log('Admin: Fetching latest Lulu status for job ID:', printJobId);
      const luluStatusResult = await getPrintJobStatus(printJobId);
      return luluStatusResult?.data || null;
    } catch (err) {
      console.error('Admin: Error fetching latest Lulu status:', err);
      // Return a structured error object instead of null
      return { 
        error: true, 
        message: err.message || 'Failed to fetch Lulu status', 
        details: err.toString() 
      };
    }
  };

  // Add state for auth and error messages
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);

  // Core loader that fetches a single page of print jobs from Firestore
  const loadPage = async ({ pageIndex, startAfterDoc = null }) => {
    try {
      console.log('Admin print jobs - loading page', pageIndex + 1, 'startAfterDoc:', startAfterDoc ? startAfterDoc.id : 'none');
      setLoading(true);
      setError(null);

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user for admin page');
        setError('Authentication required');
        setLoading(false);
        return;
      }

      console.log('Admin page - authenticated as:', user.email);
      const db = getFirestore();

      // Build Firestore query stepwise to satisfy TypeScript about constraint types
      let q = query(collection(db, 'printJobs'), orderBy('updatedAt', 'desc'));
      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }
      q = query(q, limit(pageSize + 1));

      console.log('Executing Firestore query for admin print jobs (paged):', q);
      const querySnapshot = await getDocs(q);
      console.log('Page query returned docs count (including lookahead):', querySnapshot.docs.length);

      // Determine if there is a next page by checking if we received more than pageSize docs
      const pageDocs = querySnapshot.docs.slice(0, pageSize);
      const moreDocsExist = querySnapshot.docs.length > pageDocs.length;
      setHasNextPage(moreDocsExist);

      if (pageDocs.length === 0) {
        console.log('No print jobs found for this page');
        setPrintJobs([]);
        setFilteredJobs([]);
        setUniqueStatuses(['all', 'PAID_AWAITING_FULFILLMENT']);
        setLoading(false);
        return;
      }

      // Keep track of the last visible document for this page to use as cursor for the next page
      const lastVisible = pageDocs[pageDocs.length - 1];
      setPageCursors(prev => {
        const next = [...prev];
        next[pageIndex] = lastVisible;
        return next;
      });

      console.log('All document IDs for this page:', pageDocs.map(d => d.id));

      const jobs = pageDocs.map(docSnap => {
        const data = docSnap.data();
        console.log(`Processing doc ID ${docSnap.id}:`, data);

        if (data.createdAt) {
          console.log('createdAt field exists, type:', typeof data.createdAt,
                     'isTimestamp:', data.createdAt.toDate !== undefined);
        }
        if (data.updatedAt) {
          console.log('updatedAt field exists, type:', typeof data.updatedAt,
                     'isTimestamp:', data.updatedAt.toDate !== undefined);
        }

        return {
          id: docSnap.id,
          ...data,
          printJobId: data.printJobId || null,
          paymentStatus: data.paymentStatus || 'UNKNOWN',
          status: data.status || 'UNKNOWN',
          luluApiError: null,
          luluStatusFetchFailed: false,
          createdAtFormatted: data.createdAt && typeof data.createdAt.toDate === 'function' ?
                            data.createdAt.toDate().toLocaleDateString() : 'Unknown',
          updatedAtFormatted: data.updatedAt && typeof data.updatedAt.toDate === 'function' ?
                            data.updatedAt.toDate().toLocaleDateString() :
                            (data.createdAt && typeof data.createdAt.toDate === 'function' ?
                             data.createdAt.toDate().toLocaleDateString() : 'Unknown')
        };
      });

      setPrintJobs(jobs);

      // Apply filter in memory based on the selected filter, but only within this page
      let initialFilteredJobs;
      if (statusFilter !== 'all') {
        initialFilteredJobs = jobs.filter(job => job.paymentStatus === statusFilter);
      } else {
        initialFilteredJobs = jobs;
      }
      setFilteredJobs(initialFilteredJobs);

      // Get unique payment statuses for filter based on this page
      const statuses = new Set(['all', 'PAID_AWAITING_FULFILLMENT']);
      console.log('Raw print jobs for this page:', jobs);
      if (jobs.length > 0) {
        console.log('Sample job structure for this page:', jobs[0]);
      }
      jobs.forEach(job => {
        if (job.paymentStatus) {
          statuses.add(job.paymentStatus);
        }
      });
      setUniqueStatuses(Array.from(statuses));

      setLoading(false);

      // Fetch cached details from Netlify function for just this page
      const NETLIFY_FUNCTION_URL = "/.netlify/functions/lulu-print-job-details";
      const jobsWithDetails = [];

      const newLoadingState = {};
      jobs.forEach(job => {
        const jobId = job?.printJobId || job.id;
        newLoadingState[jobId] = true;
      });
      setLoadingDetails(newLoadingState);

      for (const job of jobs) {
        const printJobId = job?.printJobId || job.id;
        if (printJobId) {
          try {
            const res = await fetch(NETLIFY_FUNCTION_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ printJobId })
            });
            if (res.ok) {
              const data = await res.json();
              console.log(`Admin: Cached print job details for ${printJobId}:`, data);

              console.log('Job object structure:', job);

              let processedData = data;

              if (data && data.detail && data.detail === "No PrintJob matches the given query.") {
                console.log(`Print job ${printJobId} not found in Lulu API`);
                const jobWithPlaceholder = {
                  ...job,
                  details: {
                    status: { name: 'UNKNOWN' },
                    line_items: [],
                  }
                };
                jobsWithDetails.push(jobWithPlaceholder);
                continue;
              }

              if (typeof data === 'string') {
                try {
                  processedData = JSON.parse(data);
                  console.log('Parsed string data:', processedData);
                } catch (e) {
                  console.error('Failed to parse data string:', e);
                }
              }

              const jobWithDetails = { ...job, details: processedData };
              console.log('Final job with details:', jobWithDetails);
              jobsWithDetails.push(jobWithDetails);
            } else {
              jobsWithDetails.push(job);
            }
          } catch (err) {
            console.error(`Error fetching details for ${printJobId}:`, err);
            jobsWithDetails.push(job);
          }
        } else {
          jobsWithDetails.push(job);
        }
      }

      // Apply filter again with details
      let detailedFilteredJobs;
      if (statusFilter !== 'all') {
        detailedFilteredJobs = jobsWithDetails.filter(job => job.paymentStatus === statusFilter);
      } else {
        detailedFilteredJobs = jobsWithDetails;
      }
      setFilteredJobs(detailedFilteredJobs);

      // Now fetch the latest status directly from Lulu API for each job on this page
      for (const job of jobsWithDetails) {
        const printJobId = job?.printJobId || job.id;
        if (printJobId) {
          try {
            const latestLuluData = await fetchLatestLuluStatus(printJobId);
            if (latestLuluData) {
              if (latestLuluData.error) {
                console.log(`Admin: Lulu API error for job ${printJobId}:`, latestLuluData.message);
                job.luluApiError = latestLuluData.message;
                job.luluStatusFetchFailed = true;
                continue;
              }
              setFilteredJobs(prev => {
                return prev.map(j => {
                  if ((j.printJobId || j.id) === printJobId) {
                    const updatedJob = {
                      ...j,
                      details: {
                        ...(j.details || {}),
                        ...latestLuluData,
                        status: latestLuluData.status || (j.details?.status || {})
                      }
                    };
                    return updatedJob;
                  }
                  return j;
                });
              });
            }

            setLoadingDetails(prev => ({
              ...prev,
              [printJobId]: false
            }));
          } catch (err) {
            console.error(`Error fetching latest Lulu status for ${printJobId}:`, err);
            setLoadingDetails(prev => ({
              ...prev,
              [printJobId]: false
            }));
          }
        } else {
          setLoadingDetails(prev => ({
            ...prev,
            [job.id]: false
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching paged print jobs:', err);
      setLoading(false);
    }
  };

  // Check authentication status on component mount
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      console.log('Current user:', user ? user.uid : 'Not logged in');
      if (user) {
        console.log('User email:', user.email);
        console.log('User metadata:', user.metadata);
      }
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Reset cursors when filter changes and load the first page
    setPageCursors([]);
    setCurrentPage(1);
    loadPage({ pageIndex: 0, startAfterDoc: null });
    
    // Set up an interval to refresh Lulu statuses every minute
    const refreshInterval = setInterval(() => {
      if (!loading && filteredJobs.length > 0) {
        // Get the first 5 jobs to avoid overloading the API
        const jobsToRefresh = filteredJobs.slice(0, 5);
        jobsToRefresh.forEach(async (job) => {
          const printJobId = job?.printJobId || job.id;
          if (printJobId) {
            try {
              const latestLuluData = await fetchLatestLuluStatus(printJobId);
              if (latestLuluData) {
                // Update only this specific job with latest data
                setFilteredJobs(prev => {
                  return prev.map(j => {
                    if ((j.printJobId || j.id) === printJobId) {
                      const updatedJob = { 
                        ...j, 
                        details: {
                          ...(j.details || {}),
                          ...latestLuluData,
                          status: latestLuluData.status || (j.details?.status || {})
                        }
                      };
                      return updatedJob;
                    }
                    return j;
                  });
                });
              }
            } catch (err) {
              console.error(`Error refreshing Lulu status for ${printJobId}:`, err);
            }
          }
        });
      }
    }, 60000); // Refresh every minute
    
    return () => clearInterval(refreshInterval);
  }, [statusFilter]);
  
  // Loading status checking helper
  const isJobLoading = (job) => {
    if (!job) return false;
    const jobId = job?.printJobId || job.id;
    return loadingDetails[jobId] ? true : false;
  };
  
  // Helper function to safely format price values
  const formatPrice = (priceInCents) => {
    if (!priceInCents && priceInCents !== 0) return 'N/A';
    // Convert to string first to handle both number and string inputs
    const priceString = String(priceInCents);
    // Check if it's a valid number string
    if (isNaN(Number(priceString))) return 'N/A';
    // Parse to a number, divide by 100, then format with 2 decimal places
    return `$${(Number(priceString) / 100).toFixed(2)}`;
  };

  // Navigation handlers that load pages from Firestore
  const handleNextPage = () => {
    if (!hasNextPage) return;
    const nextPageIndex = currentPage; // 1-based -> index for next page
    const cursorForNext = pageCursors[nextPageIndex - 1] || null;
    setCurrentPage((prev) => prev + 1);
    loadPage({ pageIndex: nextPageIndex, startAfterDoc: cursorForNext });
  };

  const handlePreviousPage = () => {
    if (currentPage <= 1) return;
    const prevPageIndex = currentPage - 2; // target page index (0-based)
    let cursorForPrev = null;
    if (prevPageIndex > 0) {
      cursorForPrev = pageCursors[prevPageIndex - 1] || null;
    }
    // prevPageIndex === 0 keeps cursor null to load the first page
    setCurrentPage((prev) => Math.max(1, prev - 1));
    loadPage({ pageIndex: prevPageIndex, startAfterDoc: cursorForPrev });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-semibold text-gray-900">All Print Jobs</h1>

      {/* Debug information */}
      <div className="mt-2 text-sm text-gray-600">
        <p>Authentication: {currentUser ? `Logged in as ${currentUser.email}` : 'Not logged in'}</p>
        <p>Jobs loaded: {printJobs.length}</p>
        <p>Filtered jobs: {filteredJobs.length}</p>
        <p>Status filter: {statusFilter}</p>
        
        {/* Lulu API debug info */}
        <div className="mt-2 bg-blue-50 p-2 rounded border border-blue-200">
          <h3 className="font-medium text-blue-800">Lulu API Debug Info</h3>
          <p>API Functions Path: <code>/.netlify/functions/lulu-*</code></p>
          <p>Details Function: <code>/.netlify/functions/lulu-print-job-details</code></p>
          <p>Status Function: <code>/.netlify/functions/lulu-print-job-status</code></p>
          <p>Environment: <code>production</code> (fixed in current implementation)</p>
        </div>
        
        {/* Debug section - shows raw data */}
        {printJobs.length > 0 && (
          <details className="mt-2 border border-gray-300 p-2 rounded">
            <summary className="font-medium">Debug: First Job Data</summary>
            <pre className="bg-gray-100 p-2 mt-1 overflow-auto max-h-48 text-xs">
              {JSON.stringify(printJobs[0], null, 2)}
            </pre>
          </details>
        )}
        
        {/* Debug section for Lulu details */}
        {printJobs.length > 0 && printJobs[0].details && (
          <details className="mt-2 border border-blue-300 p-2 rounded">
            <summary className="font-medium">Debug: Lulu API Details</summary>
            <pre className="bg-blue-50 p-2 mt-1 overflow-auto max-h-48 text-xs">
              {JSON.stringify(printJobs[0].details, null, 2)}
            </pre>
          </details>
        )}
      </div>

      {/* Error message display */}
      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No print jobs found with the selected filter.
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="p-4 bg-blue-700 text-white text-center font-bold mb-4 text-xl">
            Note: Click on any row to view job details
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Print Job ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Book ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Est. Ship Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Est. Delivery
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Amount
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-red-600 bg-red-100 uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job) => (
                    <tr 
                  key={job.id} 
                  className="hover:bg-gray-50 hover:bg-blue-100 cursor-pointer transition-colors duration-150"
                  onClick={() => window.location.href = `/admin/print-jobs/${job.id}`}
                >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {job.printJobId || job.id}
                            {job.luluStatusFetchFailed && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">
                                API Error
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {job.bookId || 'No book ID'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.userEmail || 'Unknown'}</div>
                      <div className="text-sm text-gray-500">{job.userId || 'No user ID'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.bookId || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Use the effective status that considers both payment and shipping */}
                      <div className="relative p-0.5">
                        <div className={`rounded-md px-3 py-2 ${getStatusBadgeClass(getEffectiveStatus(job)?.name || 'UNKNOWN')}`}>
                          {/* Status name */}
                          <div className="font-semibold text-sm">
                            {getEffectiveStatus(job)?.name && typeof getEffectiveStatus(job).name === 'string' ? getEffectiveStatus(job).name.replace(/_/g, ' ') : 'Unknown'}
                          </div>
                          
                          {/* Status message for additional context */}
                          {getEffectiveStatus(job).message && (
                            <div className="text-xs mt-1">
                              {getEffectiveStatus(job).message}
                            </div>
                          )}
                          
                          {/* Display API errors gracefully */}
                          {job.luluApiError && (
                            <div className="text-xs text-amber-600 mt-1">
                              {job.luluApiError.includes('No PrintJob matches') ? 
                               'Not found in Lulu API' : 'API Error: Unable to fetch status'}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.details?.estimated_shipping_dates?.dispatch_max ? 
                        new Date(job.details.estimated_shipping_dates.dispatch_max).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.details?.estimated_shipping_dates?.arrival_max ? 
                        new Date(job.details.estimated_shipping_dates.arrival_max).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.updatedAtFormatted}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatPrice(job.totalAmount || job.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <button className="inline-flex items-center justify-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                        <EyeIcon className="h-4 w-4" />
                        <span className="ml-1">View</span>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center text-sm text-gray-500">
                    No print jobs found with the selected filter.
                  </td>
                </tr>
              )}
            </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-white">
            <div className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span> — showing{' '}
              <span className="font-medium">{filteredJobs.length}</span> print jobs on this page
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="relative inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="ml-3 relative inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNextPage}
                disabled={!hasNextPage}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrintJobsPage;
